/* eslint-disable jsdoc/require-jsdoc */
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../controls/Button';
import { cancelPaypalPayment } from '../services/dashboard-service';

/**
 * PayPal checkout cancel page.
 * Displays information when a user cancels the PayPal flow.
 * @returns React component for cancel state.
 */
export default function PaypalCancelPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cancelMessage, setCancelMessage] = useState<string>('Your PayPal checkout was cancelled.');
  const [isCallingCancelEndpoint, setIsCallingCancelEndpoint] = useState<boolean>(true);

  const planName = useMemo(() => searchParams.get('planName') ?? '', [searchParams]);

  useEffect(() => {
    let isMounted = true;

    const notifyBackend = async (): Promise<void> => {
      try {
        await cancelPaypalPayment();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to notify backend about canceled PayPal checkout', error);
        if (isMounted) {
          setCancelMessage(
            'We noticed the checkout was cancelled. If this was unintentional, please try again.'
          );
        }
      } finally {
        if (isMounted) {
          setIsCallingCancelEndpoint(false);
        }
      }
    };

    notifyBackend();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-gray-100">
      <div className="w-full max-w-xl rounded-lg border border-gray-800 bg-container-500 p-8 shadow-lg">
        <div className="flex items-center gap-3">
          <span className="material-icons-outlined text-4xl text-yellow-300">cancel</span>
          <h1 className="text-2xl font-semibold text-gray-100">Payment Cancelled</h1>
        </div>
        <p className="mt-4 text-gray-400">
          {cancelMessage} {planName ? `You were checking out the ${planName} plan.` : ''}
        </p>
        {isCallingCancelEndpoint ? (
          <div className="mt-6 flex items-center gap-3 text-sm text-gray-400">
            <span className="material-icons-outlined animate-spin">autorenew</span>
            Letting our servers know…
          </div>
        ) : null}
        <div className="mt-8 flex flex-wrap gap-3">
          <Button
            name="Back to Pricing"
            theme="primary"
            size="md"
            onClick={() => navigate('/pricing')}
          />
          <Button
            name="Contact Support"
            theme="secondary-color"
            size="md"
            onClick={() => navigate('/support')}
          />
        </div>
      </div>
    </div>
  );
}
